import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Input from "../Input";
import {getLanguage} from "../../lang/helpers";

interface AutocompleteSearchProps<T> {
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  onSelect?: (selected: T) => void;
  placeholder?: string;
  containerClassName?: string;
}

const AutocompleteSearch = <T,>({
  options,
  getOptionLabel,
  getOptionKey,
  onSelect,
  placeholder = 'Search...',
  containerClassName = ''
}: AutocompleteSearchProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter and sort options based on search term
  useEffect(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    let results: T[];
    if (trimmed.length > 0) {
      results = options.filter((option) => getOptionLabel(option).toLowerCase().indexOf(trimmed) > -1);
    } else {
      results = [...options];
    }
    const sortedResults = results.sort((a, b) =>
      getOptionLabel(a).localeCompare(getOptionLabel(b), undefined, { sensitivity: 'base' })
    );
    setFilteredOptions(sortedResults);
  }, [searchTerm, options, getOptionLabel]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Handle item selection
  const handleSelectItem = (option: T) => {
    setSearchTerm('');
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`}>
      <div>
        <Input
          value={searchTerm}
          placeholder={placeholder}
          onChange={(val) => setSearchTerm(val)}
          onFocus={() => setIsOpen(true)}
          onError={() => {}}
          dropdownProps={() => {}}
          leftIcon={
            <svg
              className="w-5 h-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          }
        />
      </div>
      {
        isOpen &&
        <div className="absolute left-0 w-full bg-indigo-900 border border-indigo-700 rounded-md mt-1 z-50 max-h-60 overflow-y-auto">
          {
            filteredOptions.length > 0 ? (
              <>
              {
                filteredOptions.map((option, idx) => (
                <div
                  key={getOptionKey ? getOptionKey(option) : idx}
                  className="px-4 py-2 cursor-pointer hover:bg-indigo-800"
                  onClick={() => handleSelectItem(option)}>
                  {getOptionLabel(option)}
                </div>
                ))
              }
              </>
              ) : (
                <div className="px-4 py-2 italic">
                  {
                    lang === 'sv' ? 'Inget resultat. Be om att lägga till via support@zebrain.se' :
                      'No result. Request to add recipients via support@zebrain.se'
                  }
                </div>
            )
          }
        </div>
      }
    </div>
  );
};

export default AutocompleteSearch;
